import { useState } from "react";
import ActionBar, { type ActionBarMode } from "./UI/ActionBar";
import Input from "./UI/Input";
import { useForm, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useChangePwd } from "@core/hooks";
import { TChangePwd, type TChangePwdSchema } from "@core/types/TChangePwd";
import Checkbox from "./UI/CheckBox";

type PasswordSettingsProps = {
  onClose: () => void;
};

function PasswordSettings({ onClose }: PasswordSettingsProps) {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [mode, setMode] = useState<ActionBarMode>("view");
  const changePwdMutation = useChangePwd(isLoggingOut);

  const {
    register,
    handleSubmit,
    formState: { isDirty, errors, isSubmitting },
    reset,
  } = useForm<TChangePwdSchema>({
    resolver: zodResolver(TChangePwd),
    mode: "onBlur",
  });

  const onSubmit: SubmitHandler<TChangePwdSchema> = (data) => {
    const { old_password, new_password } = data;
    changePwdMutation.mutate(
      { old_password, new_password },
      {
        onSuccess: () => {
          setMode("view");
          reset();
        },
      },
    );
  };

  const handleModeChange = (newMode: ActionBarMode) => {
    if (newMode === "view" && mode === "edit") {
      reset();
    }
    setMode(newMode);
  };

  return (
    <ActionBar
      title="Password Settings"
      editable
      mode={mode}
      onModeChange={handleModeChange}
      saveAs="submit"
      formId="passwordForm"
      saveDisabled={!isDirty || isSubmitting}
      saveLoading={changePwdMutation.isPending}
      onSave={handleSubmit(onSubmit)}
      onClose={onClose}
      minWidth="30rem"
    >
      <form
        id="passwordForm"
        className="flex flex-col gap-4 p-2"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="w-full  border-[1px] border-neutral-200 bg-neutral-100 rounded-[8px] py-[6px] px-3 flex flex-col gap-2">
          <h5 className="text-neutral-700 font-medium">Password Management</h5>
          <h6 className="text-neutral-300">
            Change your password anytime to keep your account secure.
          </h6>
        </div>

        <div className="flex flex-col gap-4">
          <Input
            className="w-full"
            label="Current Password"
            type="password"
            {...register("old_password")}
            disabled={mode !== "edit"}
            state={errors.old_password?.message ? "error" : "default"}
            helperText={errors.old_password?.message}
          />

          <Input
            className="w-full"
            label="New Password"
            type="password"
            {...register("new_password")}
            disabled={mode !== "edit"}
            state={errors.new_password?.message ? "error" : "default"}
            helperText={errors.new_password?.message}
          />

          <Input
            className="w-full"
            label="Confirm New Password"
            type="password"
            {...register("confirmPassword")}
            disabled={mode !== "edit"}
            state={errors.confirmPassword?.message ? "error" : "default"}
            helperText={errors.confirmPassword?.message}
          />
          <div className="flex gap-2">
            <div className="flex mt-1 items-start">
              <Checkbox
                onChange={() => setIsLoggingOut((prev) => !prev)}
                checked={isLoggingOut}
                proportions={"lg"}
              />
            </div>
            <div className="flex flex-col justify-end ">
              <h5 className="text-accent-red-800">Log out from all devices</h5>
              <h6 className="text-neutral-400">
                This will end your session on this device and sign you out of
                all other devices where you’re currently logged in.
              </h6>
            </div>
          </div>
        </div>
      </form>
    </ActionBar>
  );
}

export default PasswordSettings;
